import { useFormik } from 'formik';
import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import { baseFrontServerUrl } from '~/api/provider/provider.config';
import { Button } from '~/atomic/atom/Button';
import { AddIcon } from '~/atomic/atom/icon/AddIcon';
import { TrashIcon } from '~/atomic/atom/icon/TrashIcon';
import { Input } from '~/atomic/atom/Input';
import { Field } from '~/atomic/molecula/Field';
import { identifyThePirate } from '~/atomic/page/anti-pirate/check/antiPirate.data';
import { identifyNotFoundErrorSelector } from '~/atomic/page/anti-pirate/check/antiPirate.selector';
import { TextRegStyle, TextSmallStyle } from '~/atomic/Typography';
import { useAppDispatch, useAppSelector } from '~/store';

export const CheckForm: FC = () => {
  const dispatch = useAppDispatch();
  const identifyNotFoundError = useAppSelector(identifyNotFoundErrorSelector);

  const checkForm = useFormik({
    initialValues: {
      source: '',
      file: null as File | null,
      chapter: '',
      comment: '',
    },
    validationSchema: Yup.object({
      source: Yup.string().required(),
      file: Yup.mixed().test(
        'text-not-exist',
        'Добавьте книгу или главу',
        (value) => {
          if (checkForm.values.chapter?.length > 0 || value) {
            return true;
          }
          return false;
        },
      ),
      chapter: Yup.mixed().test(
        'file-not-exist',
        'Добавьте книгу или главу',
        (value) => {
          if (checkForm.values.file || value?.length > 0) {
            return true;
          }
          return false;
        },
      ),
    }),
    onSubmit: (values, formikHelpers) => {
      if (values.file) {
        const isBookLink = values.source.includes(`${baseFrontServerUrl}/book`);
        const { length, [length - 1]: bookSlug } = values.source.split('/');

        if (bookSlug && isBookLink) {
          dispatch(identifyThePirate({
            slug: bookSlug,
            file: values.file,
            comment: values.comment,
          }));
        } else {
          formikHelpers.setFieldError('source', 'Неправильная ссылка на книгу');
        }
      } else if (values.chapter) {
        const isChapterLink = values.source.includes(`${baseFrontServerUrl}/reader`);

        const { length, [length - 1]: chapterId } = values.source.split('/');

        const isValidChapterId = typeof parseInt(chapterId, 10) === 'number';

        if (isValidChapterId && isChapterLink) {
          dispatch(identifyThePirate({
            chapterId,
            body: values.chapter,
          }));
        } else {
          formikHelpers.setFieldError('source', 'Неправильная ссылка на главу');
        }
      }
    },
  });

  useEffect(() => {
    if (identifyNotFoundError) {
      checkForm.setFieldError('source', identifyNotFoundError);
    }
  }, [identifyNotFoundError]);

  return (
    <SCForm onSubmit={checkForm.handleSubmit}>
      {!checkForm.values.chapter && (
        !checkForm.values.file
          ? (
            <SCUploadRow>
              <SCAddLabel htmlFor="file-upload">
                <SCAddIcon />
                Добавить книгу
              </SCAddLabel>
              <input
                hidden
                accept=".docx, .fb2"
                id="file-upload"
                type="file"
                name="file"
                onChange={(event) => {
                  checkForm.setFieldValue('file', event.currentTarget.files[0]);
                }}
              />
              <SCUploadDescription>
                Можно загружать fb2, docx
              </SCUploadDescription>
            </SCUploadRow>
          )
          : (
            <SCFileRow>
              <SCBookName>
                { checkForm.values?.file?.name }
              </SCBookName>
              <SCTrashIcon
                onClick={() => {
                  checkForm.setFieldValue('file', null);
                }}
              />
            </SCFileRow>
          )
      )}
      <Field
        error={checkForm.errors.source}
        label="Укажите ссылку на проверяемое произведение на Bookriver*"
        style={{ marginBottom: '30px' }}
      >
        <SCInput
          name="source"
          value={checkForm.values.source}
          onChange={checkForm.handleChange}
          onBlur={checkForm.handleBlur}
          placeholder="http://"
        />
      </Field>
      <Field
        label={(
          <>
            Поделитесь, где вы обнаружили копию произведения
          </>
        )}
        error={checkForm.errors.comment}
        style={{ marginBottom: '26px' }}
      >
        <SCInput
          name="comment"
          value={checkForm.values.comment}
          onChange={checkForm.handleChange}
          onBlur={checkForm.handleBlur}
          placeholder="http://"
        />
      </Field>
      <SCActionsRow>
        <SCSubmitButton
          htmlType="submit"
          type="primary"
        >
          Проверить
        </SCSubmitButton>
      </SCActionsRow>
    </SCForm>

  );
};

const SCTrashIcon = styled(TrashIcon)`
  cursor: pointer;
  font-size: 20px;
  color: var(--gray-color);
  
  &:hover {
    filter: brightness(1.2);
  }
`;

const SCBookName = styled.span`
  ${TextRegStyle};
  
  color: var(--primary-hover-color);
`;

const SCFileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--bg-color);
  border-radius: 2px;
  padding: 10px 12px;
  max-width: 478px;
  margin-bottom: 32px;
`;

const SCUploadDescription = styled.span`
  ${TextSmallStyle};
  margin-left: 24px;
  margin-right: auto;
  
  color: var(--gray-color);
`;

const SCAddLabel = styled.label`
  width: 100%;
  max-width: 478px;
  cursor: pointer;
  padding: 7px 16px;
  color: var(--black-color);
  background: none;
  border: 1px solid var(--border-color);
  font-family: var(--main-font);
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s;
  box-sizing: border-box;
  border-radius: 2px;
  position: relative;
  
  &:hover {
    border: 1px solid var(--black-color);
  }
`;

const SCAddIcon = styled(AddIcon)`
  font-size: 20px;
  margin-right: 10px;
`;

const SCUploadRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 40px;
  justify-content: space-between;
  align-items: center;
`;

const SCSubmitButton = styled(Button)`
  width: 230px;
`;

const SCActionsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const SCInput = styled(Input)`
  max-width: 478px !important;
`;

const SCForm = styled.form`
  
`;
